(() => {
  'use strict';

  if (window.OPSArtworkLibraryCore?.version) return;

  const DB_NAME = 'ops-artwork-scratchpad';
  const STORE_NAME = 'uploads';
  const SCHEMA_VERSION = 2;

  const uid = (prefix = 'art') => {
    if (globalThis.crypto?.randomUUID) return prefix + '_' + globalThis.crypto.randomUUID().replace(/-/g, '');
    const bytes = new Uint8Array(12);
    if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
    else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
    return prefix + '_' + Date.now() + '_' + Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const openDatabase = () => new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('Browser storage is not supported on this device.'));
      return;
    }
    const request = indexedDB.open(DB_NAME);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('My Artwork storage could not be opened.'));
  });

  let databasePromise = null;
  const database = () => {
    if (!databasePromise) databasePromise = openDatabase();
    return databasePromise;
  };

  const transact = async (mode, action) => {
    const db = await database();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      let request;
      try {
        request = action(store);
      } catch (error) {
        reject(error);
        return;
      }
      tx.oncomplete = () => resolve(request?.result);
      tx.onerror = () => reject(tx.error || request?.error || new Error('My Artwork storage operation failed.'));
      tx.onabort = () => reject(tx.error || new Error('My Artwork storage operation was cancelled.'));
    });
  };

  const hashBlob = async (blob) => {
    if (!(blob instanceof Blob) || !globalThis.crypto?.subtle) return '';
    const digest = await globalThis.crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const normaliseUsage = (usage) => {
    if (!usage || typeof usage !== 'object') return null;
    const makerId = String(usage.makerId || '').trim();
    const projectId = String(usage.projectId || '').trim();
    const route = String(usage.route || '').trim();
    if (!makerId && !projectId && !route) return null;
    return {
      makerId,
      projectId,
      route,
      source: String(usage.source || '').trim(),
      lastUsedAt: usage.lastUsedAt || new Date().toISOString(),
    };
  };

  const mergeUsage = (existing, usage) => {
    const next = Array.isArray(existing) ? existing.map((entry) => ({ ...entry })) : [];
    const candidate = normaliseUsage(usage);
    if (!candidate) return next;
    const key = candidate.makerId + '|' + candidate.projectId + '|' + candidate.route;
    const index = next.findIndex((entry) => (
      String(entry?.makerId || '') + '|' + String(entry?.projectId || '') + '|' + String(entry?.route || '')
    ) === key);
    if (index >= 0) next[index] = { ...next[index], ...candidate };
    else next.push(candidate);
    return next.slice(-50);
  };

  const normaliseRecord = (record) => {
    if (!record || typeof record !== 'object') return record;
    const id = String(record.id || record.assetId || uid('art'));
    return {
      ...record,
      id,
      assetId: String(record.assetId || id),
      kind: record.kind || 'artwork',
      schemaVersion: Math.max(Number(record.schemaVersion) || 1, SCHEMA_VERSION),
      revision: Math.max(1, Number(record.revision) || 1),
      makerUsage: Array.isArray(record.makerUsage) ? record.makerUsage : [],
      createdAt: record.createdAt || new Date().toISOString(),
      updatedAt: record.updatedAt || record.createdAt || new Date().toISOString(),
    };
  };

  const get = async (id) => {
    const record = await transact('readonly', (store) => store.get(id));
    return record ? normaliseRecord(record) : null;
  };

  const list = async () => {
    const records = await transact('readonly', (store) => store.getAll());
    return (records || []).map(normaliseRecord);
  };

  const put = async (record, action = 'update') => {
    const next = normaliseRecord(record);
    await transact('readwrite', (store) => store.put(next));
    window.dispatchEvent(new CustomEvent('ops:artwork-scratchpad-updated', {
      detail: { action, id: next.id, assetId: next.assetId },
    }));
    return next;
  };

  const saveFile = async (file, options = {}) => {
    if (!(file instanceof Blob)) throw new Error('Artwork is no longer available in this browser.');

    const preferredId = String(options.preferredId || '').trim();
    const suppliedHash = String(options.hash || '').trim();
    const hash = suppliedHash || await hashBlob(file).catch(() => '');
    let existing = preferredId ? await get(preferredId) : null;

    if (existing?.temporaryProofBridge || existing?.temporaryBuilderBridge || existing?.temporaryBridge) {
      existing = null;
    }

    if (!existing && hash) {
      const records = await list();
      existing = records.find((record) => (
        !record.temporaryProofBridge &&
        !record.temporaryBuilderBridge &&
        !record.temporaryBridge &&
        String(record.hash || '') === hash
      )) || null;
    }

    const usage = {
      makerId: options.makerId,
      projectId: options.projectId,
      route: options.route,
      source: options.source,
      lastUsedAt: new Date().toISOString(),
    };

    if (existing) {
      const next = normaliseRecord(existing);
      let changed = false;
      if (!(next.file instanceof Blob)) { next.file = file; changed = true; }
      if (!(next.originalFile instanceof Blob)) { next.originalFile = file; changed = true; }
      if (!next.hash && hash) { next.hash = hash; changed = true; }
      for (const key of ['widthPx', 'heightPx', 'hasTransparency', 'intendedWidthMm', 'intendedHeightMm']) {
        if ((next[key] === undefined || next[key] === null || next[key] === '') && options[key] !== undefined) {
          next[key] = options[key];
          changed = true;
        }
      }
      if (!next.type && (options.type || file.type)) { next.type = options.type || file.type; changed = true; }
      if (!next.size && file.size) { next.size = file.size; changed = true; }
      if (!next.name && (options.name || file.name)) { next.name = options.name || file.name; changed = true; }
      const makerUsage = mergeUsage(next.makerUsage, usage);
      if (JSON.stringify(makerUsage) !== JSON.stringify(next.makerUsage || [])) {
        next.makerUsage = makerUsage;
        next.lastUsedAt = usage.lastUsedAt;
        changed = true;
      }
      if (changed) {
        next.revision = Math.max(1, Number(next.revision) || 1) + 1;
        return put(next, 'link');
      }
      return next;
    }

    const now = new Date().toISOString();
    const id = preferredId || uid('art');
    const record = {
      id,
      assetId: id,
      kind: 'artwork',
      schemaVersion: SCHEMA_VERSION,
      revision: 1,
      name: options.name || file.name || 'artwork',
      type: options.type || file.type || 'application/octet-stream',
      size: Number(options.size) || file.size || 0,
      file,
      originalFile: file,
      widthPx: Number(options.widthPx) || 0,
      heightPx: Number(options.heightPx) || 0,
      hasTransparency: options.hasTransparency ?? null,
      intendedWidthMm: Number(options.intendedWidthMm) || null,
      intendedHeightMm: Number(options.intendedHeightMm) || null,
      notes: options.notes || '',
      tags: Array.isArray(options.tags) ? options.tags : [],
      hash,
      source: options.source || 'maker',
      makerUsage: mergeUsage([], usage),
      createdAt: now,
      updatedAt: now,
      lastUsedAt: now,
    };
    return put(record, 'save');
  };

  const recordUsage = async (id, usage = {}) => {
    if (!id) return null;
    const record = await get(id);
    if (!record) return null;
    const nextUsage = mergeUsage(record.makerUsage, { ...usage, lastUsedAt: new Date().toISOString() });
    if (JSON.stringify(nextUsage) === JSON.stringify(record.makerUsage || [])) return record;
    return put({
      ...record,
      makerUsage: nextUsage,
      lastUsedAt: new Date().toISOString(),
      revision: Math.max(1, Number(record.revision) || 1) + 1,
    }, 'link');
  };

  window.OPSArtworkLibraryCore = Object.freeze({
    version: '0.1.0-dev',
    schemaVersion: SCHEMA_VERSION,
    databaseName: DB_NAME,
    storeName: STORE_NAME,
    uid,
    hashBlob,
    normaliseRecord,
    get,
    list,
    put,
    saveFile,
    recordUsage,
  });
})();